import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Expense {
  id: number;
  category_name: string;
}

// ─── Category icon resolver ───────────────────────────────────────────────────
const KEYWORD_ICONS: [string[], string][] = [
  [["food", "meal", "lunch", "dinner", "snack", "eat", "restaurant", "grocery", "coffee"],         "🍔"],
  [["rent", "mortgage", "lease", "housing", "accommodation", "property"],                           "🏠"],
  [["bill", "electric", "electricity", "water", "hydro", "gas", "utility", "utilities", "internet"],"💡"],
  [["repair", "fix", "maintenance", "service", "mechanic"],                                         "🔧"],
  [["travel", "flight", "hotel", "trip", "transport", "transit", "bus", "train", "taxi", "uber"],   "✈️"],
  [["fuel", "gas station", "petrol", "diesel"],                                                     "⛽"],
  [["salary", "wage", "payroll", "pay"],                                                            "💼"],
  [["office", "supply", "stationery", "paper", "printer"],                                         "🖊️"],
  [["phone", "mobile", "cell", "telecom", "communication"],                                        "📱"],
  [["medical", "health", "doctor", "pharmacy", "medicine", "dental", "hospital"],                  "🏥"],
  [["insurance"],                                                                                   "🛡️"],
  [["marketing", "advertising", "ad", "promo", "promotion", "social media"],                       "📣"],
  [["software", "subscription", "saas", "license", "app", "tool"],                                 "💻"],
  [["education", "training", "course", "school", "tuition", "learning"],                           "📚"],
  [["entertainment", "event", "party", "recreation", "fun", "sport"],                              "🎉"],
  [["tax", "accounting", "audit", "legal", "lawyer", "compliance"],                                "📋"],
  [["cleaning", "janitorial", "housekeeping"],                                                     "🧹"],
  [["shipping", "courier", "delivery", "postage", "freight", "logistics"],                        "📦"],
  [["donation", "charity", "nonprofit"],                                                           "❤️"],
  [["bank", "finance", "loan", "interest", "fee"],                                                "🏦"],
];

function getCategoryIcon(name: string): string {
  const lower = name.toLowerCase();
  for (const [keywords, icon] of KEYWORD_ICONS) {
    if (keywords.some((kw) => lower.includes(kw))) return icon;
  }
  return "📂";
}

// Deterministic pastel color from category name
const PALETTE = [
  { bg: "#eef2ff", color: "#2f3891", border: "#c7d2fe" },
  { bg: "#fef3c7", color: "#d97706", border: "#fde68a" },
  { bg: "#dcfce7", color: "#16a34a", border: "#86efac" },
  { bg: "#fce7f3", color: "#db2777", border: "#fbcfe8" },
  { bg: "#e0f2fe", color: "#0284c7", border: "#bae6fd" },
  { bg: "#f3e8ff", color: "#2f3891", border: "#ddd6fe" },
  { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" },
  { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
];

function getPalette(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

// Highlight matching text
function HighlightText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <Box component="mark" sx={{ bgcolor: "#fef08a", color: "inherit", borderRadius: 0.5, px: 0.25 }}>
        {text.slice(idx, idx + query.length)}
      </Box>
      {text.slice(idx + query.length)}
    </>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 18;

const Index = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const API_URL   = process.env.REACT_APP_API_URL;

  const [expenses,     setExpenses]     = useState<Expense[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [searchQuery,  setSearchQuery]  = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [deleteTarget, setDeleteTarget] = useState<Expense | null>(null);
  const [isDeleting,   setIsDeleting]   = useState(false);
  const [hoveredId,    setHoveredId]    = useState<number | null>(null);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/view-expense-category`)
      .then((res) => setExpenses(res.data))
      .catch(() => showErrorMessage("Failed to load categories"))
      .finally(() => setLoading(false));
  }, [API_URL]);

  // ── Filter + slice ────────────────────────────────────────────────────────
  const filtered = useMemo(
    () =>
      expenses.filter((e) =>
        e.category_name.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [expenses, searchQuery]
  );

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  // Reset visible count when search changes
  useEffect(() => setVisibleCount(PAGE_SIZE), [searchQuery]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/delete-expense-category/${deleteTarget.id}`);
      setExpenses((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      showMessage("Category deleted");
      setDeleteTarget(null);
    } catch {
      showErrorMessage("Failed to delete category");
    } finally {
      setIsDeleting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2.5,
            background: "linear-gradient(135deg, #2f3891, #2f3891)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <CategoryOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20, color: "#0f172a", lineHeight: 1 }}>
              Expense Categories
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "#94a3b8", mt: 0.3 }}>
              Organize and manage your expense categories
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search categories…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: "#94a3b8" }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { bgcolor: "#fff", borderRadius: 2.5, fontSize: 13 },
            }}
            sx={{
              width: 230,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5,
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#2f3891" },
                "&.Mui-focused fieldset": { borderColor: "#2f3891" },
              },
            }}
          />

          {/* Add button */}
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 17 }} />}
            onClick={() => navigate("/expense-category/create")}
            sx={{
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              px: 2.25,
              py: 0.875,
              background: "linear-gradient(135deg, #2f3891, #2f3891)",
              boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #2f3891, #4338ca)",
                boxShadow: "0 6px 20px rgba(99,102,241,0.45)",
              },
            }}
          >
            Add Category
          </Button>
        </Box>
      </Box>

      {/* ── Stats strip ───────────────────────────────────────────────────── */}
      {!loading && expenses.length > 0 && (
        <Box sx={{ display: "flex", gap: 1.25, mb: 2.5, flexWrap: "wrap" }}>
          {[
            { label: "Total Categories", value: expenses.length, color: "#2f3891", bg: "#eef2ff" },
            { label: "Showing",          value: filtered.length,  color: "#2563eb", bg: "#eff6ff" },
          ].map(({ label, value, color, bg }) => (
            <Box key={label} sx={{ px: 1.5, py: 0.625, bgcolor: bg, borderRadius: 2, display: "flex", alignItems: "center", gap: 0.75 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color }}>{value}</Typography>
              <Typography sx={{ fontSize: 12, color: "#64748b" }}>{label}</Typography>
            </Box>
          ))}
          {searchQuery && (
            <Box sx={{ px: 1.5, py: 0.625, bgcolor: "#fef3c7", borderRadius: 2, display: "flex", alignItems: "center", gap: 0.75 }}>
              <Typography sx={{ fontSize: 12, color: "#92400e" }}>
                Results for <strong>"{searchQuery}"</strong>
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ── Loading ───────────────────────────────────────────────────────── */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
          <CircularProgress sx={{ color: "#2f3891" }} />
        </Box>
      )}

      {/* ── Empty state ───────────────────────────────────────────────────── */}
      {!loading && filtered.length === 0 && (
        <Box sx={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", py: 10, gap: 2,
        }}>
          <Box sx={{ width: 80, height: 80, borderRadius: "50%", bgcolor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography sx={{ fontSize: 36 }}>{searchQuery ? "🔍" : "📂"}</Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontWeight: 700, fontSize: 17, color: "#1e293b", mb: 0.5 }}>
              {searchQuery ? `No categories matching "${searchQuery}"` : "No categories yet"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
              {searchQuery
                ? "Try a different search term or clear the search."
                : "Create your first expense category to start organizing."}
            </Typography>
          </Box>
          {searchQuery ? (
            <Button onClick={() => setSearchQuery("")}
              sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 600, fontSize: 13, color: "#2f3891",
                border: "1px solid #c7d2fe", px: 2.5, py: 0.875, "&:hover": { bgcolor: "#eef2ff" } }}>
              Clear Search
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/expense-category/create")}
              sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 700, fontSize: 13,
                background: "linear-gradient(135deg, #2f3891, #2f3891)", px: 2.5, py: 0.875,
                boxShadow: "0 4px 14px rgba(99,102,241,0.35)" }}>
              Create First Category
            </Button>
          )}
        </Box>
      )}

      {/* ── Card grid ─────────────────────────────────────────────────────── */}
      {!loading && visible.length > 0 && (
        <>
          <Box sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 1.75,
            mb: 3,
          }}>
            {visible.map((expense) => {
              const icon    = getCategoryIcon(expense.category_name);
              const palette = getPalette(expense.category_name);
              const isHover = hoveredId === expense.id;

              return (
                <Box
                  key={expense.id}
                  onMouseEnter={() => setHoveredId(expense.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: isHover ? palette.border : "#e2e8f0",
                    p: 2.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.75,
                    cursor: "default",
                    boxShadow: isHover
                      ? "0 8px 24px rgba(0,0,0,0.10)"
                      : "0 1px 4px rgba(0,0,0,0.05)",
                    transform: isHover ? "translateY(-2px)" : "translateY(0)",
                    transition: "all 0.18s ease",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Left accent line */}
                  <Box sx={{
                    position: "absolute", left: 0, top: 0, bottom: 0,
                    width: 3, borderRadius: "3px 0 0 3px",
                    bgcolor: palette.color,
                    opacity: isHover ? 1 : 0,
                    transition: "opacity 0.18s ease",
                  }} />

                  {/* Icon badge */}
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 2,
                    bgcolor: palette.bg,
                    border: `1px solid ${palette.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                    fontSize: 22,
                    transition: "transform 0.18s ease",
                    transform: isHover ? "scale(1.08)" : "scale(1)",
                  }}>
                    {icon}
                  </Box>

                  {/* Name */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontWeight: 600, fontSize: 14, color: "#1e293b",
                      lineHeight: 1.3,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      <HighlightText text={expense.category_name} query={searchQuery} />
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>
                      ID #{expense.id}
                    </Typography>
                  </Box>

                  {/* Action buttons */}
                  <Box sx={{
                    display: "flex", gap: 0.5,
                    opacity: isHover ? 1 : 0,
                    transform: isHover ? "translateX(0)" : "translateX(6px)",
                    transition: "all 0.18s ease",
                    flexShrink: 0,
                  }}>
                    <Tooltip title="Edit category" placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/expense-category/${expense.id}`)}
                        sx={{
                          width: 32, height: 32, borderRadius: 1.5,
                          bgcolor: "#eff6ff",
                          color: "#2563eb",
                          border: "1px solid #bfdbfe",
                          "&:hover": { bgcolor: "#dbeafe" },
                          transition: "all 0.12s",
                        }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete category" placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(expense)}
                        sx={{
                          width: 32, height: 32, borderRadius: 1.5,
                          bgcolor: "#fef2f2",
                          color: "#dc2626",
                          border: "1px solid #fecaca",
                          "&:hover": { bgcolor: "#fee2e2" },
                          transition: "all 0.12s",
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* ── Load more ─────────────────────────────────────────────────── */}
          {hasMore && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                Showing {visible.length} of {filtered.length} categories
              </Typography>
              <Button
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                variant="outlined"
                sx={{
                  borderRadius: 2.5, textTransform: "none", fontWeight: 700, fontSize: 13,
                  borderColor: "#e2e8f0", color: "#475569", px: 3, py: 0.875,
                  "&:hover": { borderColor: "#2f3891", color: "#2f3891", bgcolor: "#eef2ff" },
                }}
              >
                Load More ({filtered.length - visibleCount} remaining)
              </Button>
            </Box>
          )}

          {!hasMore && filtered.length > PAGE_SIZE && (
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                All {filtered.length} categories loaded
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* ── Delete confirm dialog ─────────────────────────────────────────── */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        {deleteTarget && (
          <>
            <Box sx={{ px: 3, pt: 3, pb: 0, display: "flex", alignItems: "flex-start", gap: 1.75 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2, bgcolor: "#fef2f2",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <DeleteOutlineIcon sx={{ color: "#dc2626", fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a", mb: 0.5 }}>
                  Delete Category
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: "#475569", lineHeight: 1.55 }}>
                  Are you sure you want to delete{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "#1e293b" }}>
                    "{deleteTarget.category_name}"
                  </Box>
                  ? This action cannot be undone.
                </Typography>
              </Box>
            </Box>
            <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
              <Button
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
                sx={{
                  borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 600,
                  color: "#475569", border: "1px solid #e2e8f0",
                  "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={isDeleting}
                variant="contained"
                sx={{
                  borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 700,
                  bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" },
                  minWidth: 110,
                }}
              >
                {isDeleting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Delete"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Index;
